import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import {
  getProductSessionNames,
  updateProductSessionNames,
} from "../products-api";
import {
  normalizeSessionName,
  sessionNameErrors,
  sessionNamesDirty,
} from "../utils/session-names";

// Product-level short names for its session types (RH-1005). Lists every
// managed event used by the product's live bundles — a session type added to a
// bundle later shows up here unnamed — and saves the names on the product.
export default function SessionNamesField({
  productId,
  readOnly,
}: {
  productId: string;
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const queryKey = ["product-session-names", productId];
  const { data: rows = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getProductSessionNames(productId),
    enabled: !!productId,
  });

  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    setNames(
      Object.fromEntries(rows.map((r) => [r.managed_event_id, r.short_name ?? ""])),
    );
  }, [rows]);

  const errors = useMemo(() => sessionNameErrors(names), [names]);
  const dirty = sessionNamesDirty(rows, names);

  const save = useMutation({
    mutationFn: () => updateProductSessionNames(productId, names),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["copy-tags"] });
      toast({ title: "Session short names saved" });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't save the short names",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  if (isLoading) return <p className="text-xs text-gray-400">Loading…</p>;
  if (!rows.length) {
    return (
      <p className="text-xs text-gray-400">
        No session types yet — add sessions to this product's bundles first.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div
          key={r.managed_event_id}
          className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-2 items-start"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">
              {r.managed_event_name}
              {r.duration_minutes ? (
                <span className="text-gray-400 font-normal">
                  {" "}
                  · {r.duration_minutes} min
                </span>
              ) : null}
            </p>
            <p className="text-[11px] text-gray-400 truncate">
              In {r.bundles.join(", ")}
            </p>
          </div>
          <div>
            <Input
              className="h-8 text-sm font-mono"
              placeholder="e.g. joint"
              value={names[r.managed_event_id] ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setNames({
                  ...names,
                  [r.managed_event_id]: normalizeSessionName(e.target.value),
                })
              }
            />
            {errors[r.managed_event_id] ? (
              <p className="text-[10px] text-red-500 mt-1">
                {errors[r.managed_event_id]}
              </p>
            ) : names[r.managed_event_id] ? (
              <p className="text-[10px] text-gray-400 mt-1">
                <code>{`{{RESCHEDULE_PRICE:${names[r.managed_event_id]}}}`}</code>
              </p>
            ) : null}
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={!dirty || !!Object.keys(errors).length || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save short names"}
          </Button>
        </div>
      )}
    </div>
  );
}
